import styled from '@emotion/styled'
import { Error600, Primary100, Primary600, Black } from '@mehilainen/mds-customer/colors'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { AppointmentSearchResult, ContractRuleStatus } from '../../../__generated__/api'
import { useIsMobile } from '../../hooks/useBreakpoint'
import { scale } from '../../utils/scale'
import { BackgroundColor, HeadingTextColor } from '../Notification/Notification'
import { Text } from '../Typography/Typography'

type OHCStampStatus = ContractRuleStatus | 'primarySpecialist' | undefined

interface StampColorConfig {
  background: string
  text: string
}

const STAMP_COLOR_MAP: Record<string, StampColorConfig> = {
  [ContractRuleStatus.Disallowed]: {
    background: BackgroundColor['error'],
    text: Error600,
  },
  [ContractRuleStatus.PaymentCommitmentDisallowed]: {
    background: BackgroundColor['error'],
    text: Error600,
  },
  [ContractRuleStatus.Restriction]: {
    background: BackgroundColor['info'],
    text: HeadingTextColor['info'],
  },
  [ContractRuleStatus.ReferralRequired]: {
    background: BackgroundColor['info'],
    text: HeadingTextColor['info'],
  },
  [ContractRuleStatus.AssessmentRequired]: {
    background: BackgroundColor['info'],
    text: HeadingTextColor['info'],
  },
  [ContractRuleStatus.DentalRestriction]: {
    background: BackgroundColor['info'],
    text: HeadingTextColor['info'],
  },
  [ContractRuleStatus.CompanyInsurance]: {
    background: BackgroundColor['info'],
    text: HeadingTextColor['info'],
  },
  primarySpecialist: {
    background: Primary100,
    text: Primary600,
  },
  [ContractRuleStatus.Allowed]: {
    background: 'none',
    text: Primary600,
  },
  [ContractRuleStatus.PaymentCommitmentAllowed]: {
    background: 'none',
    text: Primary600,
  },
  [ContractRuleStatus.BookingPermitAllowed]: {
    background: 'none',
    text: Primary600,
  },
} as const

const I18N_KEYS = {
  roles: {
    TTL: 'component.appointmentList.ohc.primaryTTLSpecialist',
    TTH: 'component.appointmentList.ohc.primaryTTHSpecialist',
    TFT: 'component.appointmentList.ohc.primaryTFTSpecialist',
    TPSY: 'component.appointmentList.ohc.primaryTPSYSpecialist',
    YLEL: 'component.appointmentList.ohc.primaryYLELSpecialist',
    RATE: 'component.appointmentList.ohc.primaryRATESpecialist',
    SOSSU: 'component.appointmentList.ohc.primarySOSSUSpecialist',
  } as const,
  statuses: {
    [ContractRuleStatus.Allowed]: 'component.appointmentList.ohc.included',
    [ContractRuleStatus.PaymentCommitmentAllowed]: 'component.appointmentList.ohc.included',
    [ContractRuleStatus.BookingPermitAllowed]: 'component.appointmentList.ohc.included',
    [ContractRuleStatus.Disallowed]: 'component.appointmentList.ohc.notIncluded',
    [ContractRuleStatus.PaymentCommitmentDisallowed]: 'component.appointmentList.ohc.notIncluded',
    [ContractRuleStatus.Restriction]: 'component.appointmentList.ohc.restricted',
    [ContractRuleStatus.ReferralRequired]: 'component.appointmentList.ohc.referral_required',
    [ContractRuleStatus.AssessmentRequired]: 'component.appointmentList.ohc.assessment_required',
    [ContractRuleStatus.DentalRestriction]: 'component.appointmentList.ohc.dental_restriction',
    [ContractRuleStatus.CompanyInsurance]: 'component.appointmentList.ohc.restricted',
  } as Record<ContractRuleStatus, string>,
} as const
const PRIORITY_STATUSES = [
  ContractRuleStatus.Restriction,
  ContractRuleStatus.ReferralRequired,
  ContractRuleStatus.Disallowed,
  ContractRuleStatus.AssessmentRequired,
  ContractRuleStatus.PaymentCommitmentDisallowed,
  ContractRuleStatus.CompanyInsurance,
  ContractRuleStatus.DentalRestriction,
]

const getStampColor = (status: OHCStampStatus, type: 'background' | 'text'): string => {
  if (!status) return type === 'text' ? Black : ''

  const config = STAMP_COLOR_MAP[status]
  return config?.[type] ?? (type === 'text' ? Black : '')
}

const getStampStatus = (
  isPrimarySpecialist: boolean,
  coverage?: ContractRuleStatus
): OHCStampStatus => {
  if (!coverage) return undefined
  if (PRIORITY_STATUSES.includes(coverage)) return coverage
  if (isPrimarySpecialist) return 'primarySpecialist'
  return coverage
}

const getTranslationKey = (
  status: OHCStampStatus,
  role?: AppointmentSearchResult['primarySpecialistRole']
): string => {
  if (status === 'primarySpecialist' && role) {
    return I18N_KEYS.roles[role] || ''
  }
  if (status && status !== 'primarySpecialist') {
    return I18N_KEYS.statuses[status] || ''
  }
  return ''
}

const StampContainer = styled.div<{ isMobile: boolean; stampStatus: OHCStampStatus }>`
  border-radius: ${scale(0.75)} 0;
  position: absolute;
  padding: ${({ isMobile }) => (isMobile ? '2px 8px' : '4px 10px')};
  line-height: normal;
  bottom: 0;
  right: 0;
  background: ${({ stampStatus }) => getStampColor(stampStatus, 'background')};

  ${Text} {
    color: ${({ stampStatus }) => getStampColor(stampStatus, 'text')};
  }
`

interface OHCStampProps {
  appointment: AppointmentSearchResult
}

const OHCStamp: FC<OHCStampProps> = ({ appointment }) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const stampStatus = getStampStatus(
    appointment.primarySpecialistRole !== undefined,
    appointment.isCoveredByOHC
  )

  if (!stampStatus) return null

  const translationKey = getTranslationKey(stampStatus, appointment.primarySpecialistRole)

  return (
    <StampContainer isMobile={isMobile} stampStatus={stampStatus}>
      <Text $size={200} $weight={stampStatus === 'primarySpecialist' ? 'Medium' : 'Regular'}>
        {t(translationKey)}
      </Text>
    </StampContainer>
  )
}

export default OHCStamp
